import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ZoekDocumentZittingService, IDocumentZittingSearchArgs, IDocumentZittingResultItem, IPeriodeFilter } from '../zoek-document-zitting.service';
import { ActivatedRoute, Router } from '@angular/router';
import { map, switchMap, first, filter, share } from 'rxjs/operators';
import { Observable, Subject, combineLatest } from 'rxjs';
import { MenuService } from '../../shared/menu.service';
import { cacheTimeMax, IStateOptions2 } from '../../shared/state';
import { ICodeIdOmschrijving, GlobalDataService } from '../../shared/global-data.service';
import { AppService } from '../../shared/app.service';

import { jumpToAnchor } from 'src/app/shared/helpers';
import { createCmdFactory } from '@cevi/rxjscommands';
import { ICommonSearchArgs } from 'src/app/shared/common-search-args';
import { FormsModule } from '@angular/forms';
import { NgIf, NgFor, AsyncPipe, DatePipe } from '@angular/common';

const pageSize = 20;

interface IZoekDocZittingVm {
  periodeFilters: IPeriodeFilter[];
  soortenZitting: ICodeIdOmschrijving[];
  searchArgs: ICommonSearchArgs;
  searchResults: IDocumentZittingResultItem[];
  hasMoreResults: boolean;
}

const getDefaultSearchArgs = (): ICommonSearchArgs => ({
  SearchFor: '',
  PeriodeFilter: 'y',
  CombineWordsWith: ' or ',
  SoortZittingId: null,
  CurrentPage: 0,
  VanJaar: null,
  TotJaar: null
});


const emptyVm: IZoekDocZittingVm = {
  periodeFilters: [],
  soortenZitting: [],
  searchArgs: getDefaultSearchArgs(),
  hasMoreResults: false,
  searchResults: []
};

@Component({
    selector: 'app-zoek-document-zitting',
    templateUrl: './zoek-document-zitting.component.html',
    styleUrls: ['./zoek-document-zitting.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, FormsModule, NgFor, AsyncPipe, DatePipe],
    providers:[ZoekDocumentZittingService]
})
export class ZoekDocumentZittingComponent implements OnInit {

  searchCmdSubj = new Subject<boolean>();
  searchMoreCmdSubj = new Subject<boolean>();
  openDocumentSubj = new Subject<IDocumentZittingResultItem>();
  detailZittingSubj = new Subject<IDocumentZittingResultItem>();
  resetFilterSubj = new Subject<boolean>();

  vm$: Observable<IZoekDocZittingVm>;
  vm: IZoekDocZittingVm;

  constructor(private zoekDocumentZittingService: ZoekDocumentZittingService,
    private appService: AppService,
    private menuService: MenuService,
    private route: ActivatedRoute,
    private globalDataService: GlobalDataService,
    private router: Router) {

    this.appService.routing.createCurrentHistoryItem('Zoekresultaten');

    const cmdFactory = createCmdFactory<IZoekDocZittingVm>();
    cmdFactory.addMapAction(this.setSoortenZittingAction$, 'setSoortenZitting');
    cmdFactory.addMapAction(this.searchResultFromQueryParamsAction$, 'searchResultFromQueryParams');
    cmdFactory.addMapAction(this.refreshDataAction$, 'refreshData');
    cmdFactory.addMapAction(this.navigateFromSearchAction$, 'navigateFromSearch');
    cmdFactory.addMapAction(this.navigateFromSearchMoreAction$, 'searchMore');
    cmdFactory.addMapAction(this.openDocumentAction$, 'openDocument');
    cmdFactory.addMapAction(this.navigateFromResetFilterAction$, 'resetFilter');
    cmdFactory.addMapAction(this.navigateFromFragment$, 'navigatefromfragment');
    this.vm$ = cmdFactory.merge(emptyVm);
    //console.log('newSearch', this.route.snapshot.parent.data.newSearch);
  }

  private setSoortenZittingAction$ = this.globalDataService.getSoortenZitting().pipe(
    map(soortenZitting => (vm: IZoekDocZittingVm) => ({
      ...vm,
      soortenZitting,
      periodeFilters: this.zoekDocumentZittingService.getPeriodeFilters()
    })
    )
  );

  private searchResultFromQueryParams$ = this.route.paramMap.pipe(
    map(paramMap => this.appService.routing.paramMapToSearchArgs(paramMap, getDefaultSearchArgs())),
    switchMap(searchArgs => this.searchDocuments(searchArgs).pipe(map(results => ({ results, searchArgs })))),
    share() // 2 subscriptions !!
  );

  private searchResultFromQueryParamsAction$ = this.searchResultFromQueryParams$.pipe(
    map(data => (vm: IZoekDocZittingVm) => {
      return {
        ...vm,
        searchArgs: data.searchArgs,
        searchResults: this.processSearchResults(data.results.items),
        hasMoreResults: data.results.hasMoreResults
      };
    })
  );

  private mapToZoekNavigation = (vm: IZoekDocZittingVm) => {
    if (!vm.searchArgs.SearchFor) { return vm; }
    const options: IStateOptions2 = { allowFromCache: false, cacheTime: cacheTimeMax, concat: false, page: 0, pageSize };
    const newSearchArg: ICommonSearchArgs = { ...vm.searchArgs, CurrentPage: 0 };
    this.router.navigate(['zoekdocumentzitting', newSearchArg], { state: options });
    return vm;
  }

  // werkt nog niet
  private refreshDataAction$ = this.appService.refresh$.pipe(
    map(_ => this.mapToZoekNavigation)
  );

  private openDocumentAction$ = this.openDocumentSubj.pipe(
    map(item => (vm: IZoekDocZittingVm) => {
      this.menuService.openDocument('zitting', item.DocumentZittingId, item.Titel, item.Verwijzing);
      return vm;
    })
  );

  private navigateFromResetFilterAction$ = this.resetFilterSubj.pipe(
    map(_ => (vm: IZoekDocZittingVm) => {
      const newSearchArg: IDocumentZittingSearchArgs = { SearchFor: '', CurrentPage: 0, CombineWordsWith: ' or ', PeriodeFilter: 'y', SoortZittingId: null };
      this.appService.routing.navigateToZoekDocumentenZitting(newSearchArg, false);
      return vm;
    })
  );

  private navigateFromFragment$ = combineLatest([
    this.searchResultFromQueryParams$.pipe(first()),
    this.route.fragment.pipe(filter(fragment => fragment > ''))
  ]).pipe(
    map(([_,fragment])=> (vm:IZoekDocZittingVm)=>{
      jumpToAnchor(fragment);
      return vm;      
    })
  )

  private navigateFromSearchAction$ = this.searchCmdSubj.pipe(
    map(_ => this.mapToZoekNavigation)
  );

  private navigateFromSearchMoreAction$ = this.searchMoreCmdSubj.pipe(
    map(_ => (vm: IZoekDocZittingVm) => {
      const page = vm.searchArgs.CurrentPage + 1;
      const options: IStateOptions2 = { allowFromCache: false, concat: true, page };
      const newSearchArg: ICommonSearchArgs = { ...vm.searchArgs, CurrentPage: page };
      this.router.navigate(['zoekdocumentzitting', newSearchArg], { state: options });
      return vm;
    })
  );

  public navigateToZoekAgendapunten = (searchArg: ICommonSearchArgs) => {
    this.appService.routing.navigateToZoekAgendapunten(searchArg);
  }

  public navigateToZoekNotities = (searchArg: ICommonSearchArgs) => {
    this.appService.routing.navigateToZoekNotities(searchArg);
  }

  public navigateToDetailZitting = (item: IDocumentZittingResultItem) => {
    this.appService.routing.setFragment(`z_${item.ZittingId}`);
    this.appService.routing.navigateToDetailZitting(item.ZittingId);
    return false;
  }

  public getResultId = (_: number, result: IDocumentZittingResultItem) => result.DocumentZittingId;

  private searchDocuments(searchParams: IDocumentZittingSearchArgs) {
    const searchArgs = { ...{ SearchFor: '', CurrentPage: 0 }, ...searchParams };
    return this.zoekDocumentZittingService.zoek(searchArgs);
  }

  private processSearchResults(result: IDocumentZittingResultItem[]) {
    let lastZittingId = -1;
    result.forEach(r => {
      r.isZittingBreak = r.ZittingId !== lastZittingId;
      lastZittingId = r.ZittingId;
    });
    return result;
  }

  wisZoekFilters=()=>{
    this.resetFilterSubj.next(true);
    return false;
  }
  
  public openDocument=(document:IDocumentZittingResultItem)=>{
    this.openDocumentSubj.next(document);
    return false;
  }

  ngOnInit() {
  }

}
